/**
 * Thin SPA → insights-api HTTP helpers.
 *
 * Every call goes cross-origin to `/api/tenant/*` on insights-api, through
 * {@link apiFetch}, carrying the session cookie. There is no dashboard backend
 * in this path any more: insights-api is the OIDC relying party, holds the IdP
 * tokens server-side, and hands the browser only an opaque HttpOnly session
 * handle. See `docs/legacy/plans/dashboard-app-static.md`.
 *
 * **The browser still never holds an IdP token** — that property is preserved,
 * it just moved. What changed is *which* server keeps it.
 *
 * Consequences worth knowing before editing this file:
 *   - Requests are cross-origin, so anything non-GET needs the CSRF header
 *     (`apiFetch` adds it) and the method must be in insights-api's CORS
 *     `methods` list. `PATCH` was missing there until `2026.18.1.1` precisely
 *     because a same-origin proxy used to hide the gap.
 *   - A 401 means the session is gone, and `apiFetch` restarts the login flow.
 *     Callers do not need to handle re-authentication.
 *
 * Token mint still goes to `/api/tenant/tokens`, now directly rather than via a
 * dashboard-side handler. The minted token is short-lived (1h) and scoped to a
 * single insight; the SPA hands it to `<lucent-insight>` as the `token`
 * attribute. Embed-token calls under `/api/embed/*` were always cross-origin
 * direct to insights-api from the runtime bundle and are unchanged.
 */

import { CSRF_HEADER, INSIGHTS_API_URL } from "./env.ts";

/** Shape returned by `/api/tenant/tokens`. */
export interface MintedToken {
  token: string;
  /** Epoch seconds the token expires at. */
  expires_at: number;
}

/**
 * Definition of an insight's "primary stat" — the headline tile rendered
 * next to the row on `/insights` and at the top of the insight detail
 * page. Lifted at save time from `sbi.primary.*` calls in the SBI source
 * by `insights-api`. `null` when the source has no declaration.
 *
 * See `docs/plans/insight-primary-stat.md` for the full surface. The
 * discriminated union matches the persisted shape exactly so SPA
 * components can render without re-parsing.
 */
export type PrimaryStat =
  | {
      kind: "kpi";
      label: string;
      sql: string;
      format?: string;
    }
  | {
      kind: "bar" | "line";
      label: string;
      sql: string;
      x_col: string;
      y_col: string;
    };

/** Error codes attached to a failed seed. Mirrors the insights-api shape. */
export type PrimaryStatErrorCode =
  | "no_rows"
  | "sql_rejected"
  | "db_error"
  | "timeout"
  | "shape_mismatch"
  | "tenant_scope_mismatch";

/**
 * Last-computed value paired with a `PrimaryStat`. Discriminated on
 * `status`; the OK variants split by whether the underlying `kind` is
 * `kpi` (scalar) or `bar`/`line` (series). Renderers read `kind` from
 * the *definition*, not the value — see plan §3.3.
 */
export type PrimaryStatValue =
  | { status: "ok"; value: number | string }
  | {
      status: "ok";
      series: Array<{ x: unknown; y: number }>;
      truncated?: boolean;
    }
  | { status: "error"; code: PrimaryStatErrorCode; message: string };

/**
 * One group an insight has been shared with, as it appears on
 * `InsightSummary.shared_groups`.
 *
 * The name is carried alongside the id so a share chip renders without
 * resolving it against `GET /api/tenant/groups` — the list endpoint
 * doesn't have to be fetched at all unless the owner opens the sharing
 * panel to change the selection.
 */
export interface InsightGroupShare {
  id: string;
  name: string;
}

/**
 * A group the caller's client has defined, from `GET /api/tenant/groups`.
 *
 * Groups are created and populated by operators in insights-admin; the
 * dashboard can only read them and attach them to an insight the caller
 * owns. Membership is deliberately not exposed.
 */
export interface UserGroup {
  id: string;
  name: string;
  description: string | null;
}

/** Subset of insight fields the SPA cares about for list/detail views. */
export interface InsightSummary {
  id: string;
  title: string;
  description: string | null;
  current_version: number;
  created_at: string;
  updated_at: string;
  tags: string[];
  /**
   * Primary-stat definition for the current version. `null` when the
   * source has no `sbi.primary.*` declaration; renderer reserves the
   * tile slot for layout stability but paints nothing.
   */
  primary_stat: PrimaryStat | null;
  /**
   * Last seeded value matching `primary_stat`. `null` until the first
   * seed lands, or when there is no declaration.
   */
  primary_stat_value: PrimaryStatValue | null;
  /**
   * ISO 8601 timestamp of when the seed last ran. Distinct from
   * `updated_at` — the renderer surfaces both as separate stamps.
   */
  primary_stat_computed_at: string | null;
  /**
   * Resolved cadence (insight override → client default) in seconds.
   * Drives the "refresh cadence: daily" stamp on the header tile.
   * Plan §8.2.
   */
  primary_stat_cadence_seconds: number | null;
  /**
   * Pinned error code when the most recent sample is `status:'error'`.
   * Drives the list-tile error-state glyph (the `!` indicator) and the
   * header tile's "Data refresh failed" / "Definition changed"
   * differentiation. Plan §8.1 / §8.2.
   */
  primary_stat_error_code: PrimaryStatErrorCode | null;
  /**
   * Direction of the headline scalar's most recent change, filtered
   * to the current definition's lineage. `null` when there's only one
   * sample, when prev crosses a definition_hash boundary, or when the
   * sample is an error. Plan §8.1.
   */
  primary_stat_delta_direction: "up" | "down" | "flat" | null;
  /**
   * Numeric delta (current scalar − previous scalar). Now on both
   * the list and detail-page projections — the list table renders
   * the number alongside the direction icon; the header tile uses
   * it for the labelled "vs previous refresh" string.
   */
  primary_stat_delta_scalar: number | null;
  /**
   * Up to 7 most recent same-lineage daily rollup points. Drives the
   * header sparkline (kpi only on the SPA side). Only on the
   * single-insight read; list rows omit. Plan §8.2.
   */
  primary_stat_sparkline?: Array<{ day: string; scalar: number | null }>;
  /**
   * Original creator (author of `version=1`). `*_email` / `*_name` are
   * the LEFT-JOIN enrichment from the `users` registry — fall back to
   * `created_by` (raw JWT sub) when null. All three are null for rows
   * whose v1 predates author tracking.
   */
  created_by: string | null;
  created_by_email: string | null;
  created_by_name: string | null;
  /**
   * Latest editor (author of the current version). Equals `created_by`
   * until the insight is edited.
   */
  updated_by: string | null;
  updated_by_email: string | null;
  updated_by_name: string | null;
  /**
   * Per-insight owner (FK → `users.id` on insights-api). Distinct from
   * `created_by`: the creator never changes, but `owner_id` can be
   * reassigned. `*_email` / `*_name` follow the same LEFT-JOIN
   * fallback contract as the other enrichment pairs. Legacy rows
   * whose v1 author wasn't in the registry land with all three null.
   */
  owner_id: string | null;
  owner_email: string | null;
  owner_name: string | null;
  /**
   * When true, the insight is visible only to its owner plus members
   * of any group in `shared_groups`. Defaults to **true** on create —
   * an insight is private until its author deliberately shares it.
   * Rows created before that default changed keep whatever visibility
   * they had.
   */
  is_private: boolean;
  /**
   * Groups this insight has been shared with. Read grants only —
   * membership never confers edit or delete. Empty for most rows, and
   * only meaningful while `is_private` is true (a tenant-wide insight
   * is already visible to everyone).
   *
   * Operators define groups in insights-admin; the owner picks from
   * `GET /api/tenant/groups` in the sharing panel.
   */
  shared_groups: InsightGroupShare[];
  /**
   * When true, the client-facing dashboard is allowed to save edits
   * to this insight. Defaults to true on create.
   */
  allow_client_edit: boolean;
  /**
   * External-embed gate. When true the insight is reachable via a
   * service-key-minted embed token (`POST /api/tenant/embed-tokens` →
   * `Authorization: Bearer <embed_token>`). When false those mints
   * return `INSIGHT_NOT_PUBLISHED` (403). User-JWT preview tokens (the
   * dashboard's own embed) are not gated. Defaults to false on create.
   */
  is_published: boolean;
  /**
   * The pinned version served to external embeds. NULL = never
   * published. Editing a new version does NOT advance the pin — the
   * owner must explicitly re-publish. When this is less than
   * `current_version`, the dashboard surfaces an "unpublished changes"
   * notice with a "Publish current version" affordance.
   */
  published_version: number | null;
  /**
   * Server-stamped flag: true iff the row's `owner_id` matches the
   * caller's `users.id`. Computed at the route layer because the SPA
   * doesn't carry its own registry UUID — only the JWT `sub`. Drives
   * owner-only affordances (sharing/publish controls, "Mine" filter
   * chip).
   */
  is_mine: boolean;
}

/** Version metadata for the diff/history view. */
export interface VersionMeta {
  version: number;
  message: string | null;
  /** FK to `users.id` on the insights-api side; `null` for legacy rows. */
  author_id: string | null;
  parent_version: number | null;
  is_rollback_of: number | null;
  created_at: string;
}

/**
 * Error thrown by `getJson` so callers can branch on HTTP status
 * codes without re-parsing the message. The view-load path uses
 * this to distinguish "view exists but isn't mine" (403) and
 * "view doesn't exist" (404) from genuine 5xx failures.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    body: string,
  ) {
    super(`GET ${path} → ${status}: ${body.slice(0, 200)}`);
    this.name = "ApiError";
  }
}

/** Methods that can't change state, and so need no CSRF header. */
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * Send the browser to the login flow, preserving where it was going.
 *
 * A full-page navigation, not a `fetch` — the flow ends in a redirect to the
 * IdP, and following that with XHR would hit CORS and lose the top-level
 * context the IdP's own SSO cookie needs.
 *
 * `replace` rather than `assign` so the back button doesn't land the user on a
 * page that will only bounce them here again.
 */
const goToLogin = (): void => {
  const returnTo = window.location.pathname + window.location.search;
  window.location.replace(
    `${INSIGHTS_API_URL}/api/tenant/auth/login?return_to=${encodeURIComponent(returnTo)}`,
  );
};

/**
 * The single seam between this SPA and insights-api.
 *
 * Three responsibilities, and deliberately no more:
 *
 *   1. **Prefix** the path with the API origin. Paths are passed leading-slash
 *      relative, exactly as they were when the dashboard's own backend proxied
 *      them, so no call site had to change shape.
 *   2. **`credentials: "include"`.** The session cookie lives on the
 *      insights-api origin; without this the browser attaches nothing and
 *      every call 401s.
 *   3. **CSRF header on state-changing methods.** `SameSite` is no protection
 *      here — the dashboard and insights-api are sibling subdomains of one
 *      registrable domain, so they are *same-site* and the cookie rides along
 *      on these cross-origin calls regardless.
 *
 * **What it deliberately does not do: token handling.** No refresh, no retry,
 * no single-flight, no terminal-vs-transient error taxonomy. All of that lives
 * server-side now, inside insights-api's session layer. A 401 reaching here
 * means the session is genuinely gone, so the only correct response is to
 * restart the login flow — which is why this wrapper is ~20 lines rather than
 * the ~200 an in-browser token manager would need.
 */
export const apiFetch = async (
  path: string,
  init: RequestInit = {},
): Promise<Response> => {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (!SAFE_METHODS.has(method)) headers.set(CSRF_HEADER, "1");

  const res = await fetch(`${INSIGHTS_API_URL}${path}`, {
    ...init,
    method,
    headers,
    credentials: "include",
  });

  if (res.status === 401) {
    goToLogin();
    // The navigation above is asynchronous, so this line still runs. Throwing
    // stops callers from treating the 401 body as data during the brief window
    // before the page unloads.
    throw new ApiError(401, path, "session expired");
  }

  return res;
};

/** GET wrapper that throws on non-2xx with a message including the body. */
const getJson = async <T>(path: string): Promise<T> => {
  const r = await apiFetch(path);
  if (!r.ok) {
    const body = await r.text().catch(() => "");
    throw new ApiError(r.status, path, body);
  }
  return (await r.json()) as T;
};

/** POST wrapper. */
const postJson = async <T>(path: string, body: unknown): Promise<T> => {
  const r = await apiFetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`POST ${path} → ${r.status}: ${text.slice(0, 200)}`);
  }
  return (await r.json()) as T;
};

/**
 * Owner-only patch payload for the sharing & visibility panel. Each
 * field is optional — omitted fields are left untouched. The route
 * layer applies `canEdit` first and rejects non-owner attempts to set
 * any of these with a 403 + `code: "owner_only_fields"` body.
 *
 * The dashboard's edit panel sends *only* the changed fields. A PUT
 * still requires `content`, so this helper passes the row's current
 * source through unchanged when patching the sharing flags.
 */
export interface SharingPatch {
  is_private?: boolean;
  is_published?: boolean;
  allow_client_edit?: boolean;
  /**
   * REPLACES the set of groups this insight is shared with — not a
   * delta. `[]` revokes every group share.
   *
   * Orthogonal to `is_private`: the panel sends both together
   * (`{ is_private: true, group_ids: [...] }` for "shared with
   * specific groups"), because a group share on a tenant-wide insight
   * is inert rather than an error. A group id the caller's client
   * doesn't own is a 422.
   */
  group_ids?: string[];
}

/**
 * Patch the sharing/publish flags on an insight via the dedicated
 * PATCH endpoint. Doesn't bump a version (so toggling a visibility
 * flag doesn't pollute the version history). Returns the refreshed
 * row. Throws with the body verbatim on non-2xx so callers can branch
 * on the `owner_only_fields` 403 body.
 */
export const updateInsightSharing = async (
  id: string,
  patch: SharingPatch,
): Promise<InsightSummary> => {
  const r = await apiFetch(`/api/tenant/insights/${encodeURIComponent(id)}/sharing`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new ApiError(r.status, `PATCH /api/tenant/insights/${id}/sharing`, text);
  }
  return (await r.json()) as InsightSummary;
};

/**
 * Caller-controlled chrome flags persisted on the embed-token row.
 * The dashboard's per-insight viewer opts both on so authors see the
 * Queries + Versions debug tabs; embedding integrators that mint
 * tokens via their own backend leave them off by default.
 */
export interface MintInsightTokenOpts {
  showQueries?: boolean;
  showVersions?: boolean;
  showCode?: boolean;
}

/** Mint a 1-hour Bearer token scoped to `insightId`. */
export const mintInsightToken = async (
  insightId: string,
  opts: MintInsightTokenOpts = {},
): Promise<MintedToken> => {
  const body: Record<string, unknown> = { insight_id: insightId };
  if (opts.showQueries !== undefined) body.show_queries = opts.showQueries;
  if (opts.showVersions !== undefined) body.show_versions = opts.showVersions;
  if (opts.showCode !== undefined) body.show_code = opts.showCode;
  return postJson<MintedToken>("/api/tenant/tokens", body);
};

/**
 * Roster entry for the author filter dropdown. Backend returns every
 * v1 creator regardless of how many insights they've authored — see
 * `InsightsManager.listAuthors`. `email` / `name` are nullable when
 * the per-tenant users registry hasn't enriched the row; consumers
 * fall back to `id` for display.
 */
export interface AuthorSummary {
  /** FK → `users.id` on insights-api. The value to send back as `?author=`. */
  id: string;
  email: string | null;
  name: string | null;
  /** Non-deleted insights this user authored as v1. Always >= 1. */
  count: number;
}

/**
 * List all non-deleted insights, optionally filtered by a tag and/or
 * v1 creator. Filters compose on the backend.
 */
export const listInsights = async (
  opts?: { tag?: string; author?: string },
): Promise<InsightSummary[]> => {
  const params = new URLSearchParams();
  if (opts?.tag) params.set("tag", opts.tag);
  if (opts?.author) params.set("author", opts.author);
  const qs = params.toString();
  const url = qs ? `/api/tenant/insights?${qs}` : "/api/tenant/insights";
  const data = await getJson<{ insights: InsightSummary[] }>(url);
  return data.insights;
};

/**
 * Fetch the groups defined for the caller's client, alphabetically by
 * name. Backs the sharing panel's audience picker.
 *
 * An empty array is a normal state, not an error: a client whose
 * operator hasn't defined any groups simply has "private" and "shared
 * with everyone" as its only two options.
 */
/**
 * A tenant user's capability tier, as insights-api projects it from
 * the caller's verified token.
 *
 *   user    read existing insights.
 *   editor  additionally author insights, use the data explorer, and
 *           use the MCP tool.
 */
export type TenantRole = "user" | "editor";

/** Shape of `GET /api/tenant/clients/me`. */
export interface ClientMe {
  client_key: string;
  name: string | null;
  role: TenantRole;
}

/**
 * Who the caller is within their tenant, including their tier.
 *
 * The SPA uses `role` only to hide affordances it knows will 403.
 * insights-api enforces on every route independently, so a stale copy
 * in a long-lived tab costs a rejected request, never unintended
 * access. Treat a missing or unrecognised value as the read-only tier
 * — the same fail-closed direction the API takes.
 */
export const getClientMe = async (): Promise<ClientMe> => {
  const data = await getJson<Partial<ClientMe>>("/api/tenant/clients/me");
  return {
    client_key: data.client_key ?? "",
    name: data.name ?? null,
    role: data.role === "editor" ? "editor" : "user",
  };
};

export const listGroups = async (): Promise<UserGroup[]> => {
  const data = await getJson<{ groups: UserGroup[] }>("/api/tenant/groups");
  return data.groups;
};

/**
 * Fetch the author roster for the InsightList filter dropdown. Every
 * v1 creator is returned, ordered by insight count desc then display
 * name asc — see `InsightsManager.listAuthors` on insights-api.
 */
export const listAuthors = async (): Promise<AuthorSummary[]> => {
  const data = await getJson<{ authors: AuthorSummary[] }>(
    "/api/tenant/insights/authors",
  );
  return data.authors;
};

/** Read insight metadata (no source). */
export const getInsight = async (id: string): Promise<InsightSummary> =>
  getJson<InsightSummary>(`/api/tenant/insights/${encodeURIComponent(id)}`);

/** Version history newest-first. */
export const listVersions = async (id: string): Promise<VersionMeta[]> => {
  const data = await getJson<{ versions: VersionMeta[] }>(
    `/api/tenant/insights/${encodeURIComponent(id)}/versions`,
  );
  return data.versions;
};

/** Unified diff between two versions. */
export const getDiff = async (id: string, from: number, to: number): Promise<string> => {
  const data = await getJson<{ diff: string }>(
    `/api/tenant/insights/${encodeURIComponent(id)}/diff?from=${from}&to=${to}`,
  );
  return data.diff;
};

/**
 * Trigger a PDF export for an insight, forwarding the viewer's current
 * filter / drilldown state so the rendered PDF reflects what's on
 * screen — not the embed's defaults.
 *
 * Posts to dashboard-side `/api/tenant/insights/:id/pdf`, which Fastify
 * proxies through to insights-api. The response is binary PDF;
 * caller is expected to attach the returned `Blob` to a hidden
 * `<a download>` to drive the actual file save.
 *
 * Errors throw with the response body (truncated) so the calling
 * view can toast a useful message rather than a generic "failed".
 */
export const exportInsightPdf = async (
  id: string,
  state: {
    host: Record<string, unknown>;
    author: Record<string, unknown>;
  },
): Promise<Blob> => {
  const r = await apiFetch(`/api/tenant/insights/${encodeURIComponent(id)}/pdf`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ state }),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(
      `POST /api/tenant/insights/${id}/pdf → ${r.status}: ${text.slice(0, 200)}`,
    );
  }
  return r.blob();
};

// ─── User views (saved-view feature) ─────────────────────────────────

/**
 * Persisted view of an insight — a `(insight_id + name + state)`
 * snapshot owned by the current user. Mirrors `UserViewRow` on
 * insights-api after JSON-ification.
 *
 * `state` carries the embed's namespaced `{host, author}` envelope.
 * `version` is the `insights.current_version` stamped at save time;
 * `currentVersion` is the live value at read time. `isStale` is the
 * server-derived `version < currentVersion` boolean — the UI uses
 * it directly rather than comparing on the client.
 */
export interface UserView {
  id: string;
  insightId: string;
  name: string;
  state: { host: Record<string, unknown>; author: Record<string, unknown> };
  version: number;
  currentVersion: number;
  isStale: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Body shape for POST / PATCH (state is namespaced). */
export interface UserViewWriteBody {
  name?: string;
  state?: { host: Record<string, unknown>; author: Record<string, unknown> };
}

/** Every view the caller owns, optionally narrowed to one insight. */
export const listUserViews = async (
  opts: { insightId?: string } = {},
): Promise<UserView[]> => {
  const qs = opts.insightId
    ? `?insight_id=${encodeURIComponent(opts.insightId)}`
    : "";
  const data = await getJson<{ views: UserView[] }>(`/api/tenant/users/me/views${qs}`);
  return data.views;
};

/** Views for a specific insight (caller-owned). Same shape as the filtered list call. */
export const listInsightViews = async (insightId: string): Promise<UserView[]> => {
  const data = await getJson<{ views: UserView[] }>(
    `/api/tenant/insights/${encodeURIComponent(insightId)}/views`,
  );
  return data.views;
};

/**
 * Load a single view by its surrogate id. Surfaces `ApiError` on
 * 403/404 so the inaccessible-view banner can show the right copy
 * without parsing the message.
 */
export const getUserView = async (id: string): Promise<UserView> => {
  // No GET /api/tenant/views/:id on insights-api — fall back to filtering
  // /api/tenant/users/me/views by insight is not viable since we don't know
  // the insight up-front. The view-load flow uses `?view=:id` so the
  // SPA already has the id; resolving via list is acceptable since
  // the typical user has tens of views, not thousands.
  //
  // If perf becomes a concern, add a dedicated `GET /api/tenant/views/:id`
  // to insights-api — the manager already has `read(userId, id)`.
  const data = await getJson<{ views: UserView[] }>(
    `/api/tenant/users/me/views`,
  );
  const hit = data.views.find((v) => v.id === id);
  if (!hit) {
    throw new ApiError(404, `/api/tenant/views/${id}`, "view not found");
  }
  return hit;
};

/** Create a new view under the given insight. 409 on `(insight, name)` collision. */
export const createUserView = async (
  insightId: string,
  body: { name: string; state: UserView["state"] },
): Promise<UserView> =>
  postJson<UserView>(
    `/api/tenant/insights/${encodeURIComponent(insightId)}/views`,
    body,
  );

/**
 * Patch an existing view. Either `name`, `state`, or both. When `state`
 * is in the body the server re-stamps `version` from
 * `insights.current_version` — this is what the Update-view button
 * uses to refresh the staleness signal at the same time as the snapshot.
 */
export const updateUserView = async (
  id: string,
  body: UserViewWriteBody,
): Promise<UserView> => {
  const r = await apiFetch(`/api/tenant/views/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new ApiError(r.status, `/api/tenant/views/${id}`, text);
  }
  return (await r.json()) as UserView;
};

/** Delete a view. 204 on success; treated as success even if the id was unknown. */
export const deleteUserView = async (id: string): Promise<void> => {
  const r = await apiFetch(`/api/tenant/views/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok && r.status !== 404) {
    const text = await r.text().catch(() => "");
    throw new ApiError(r.status, `/api/tenant/views/${id}`, text);
  }
};

// ─── Data Explorer ────────────────────────────────────────────────

/**
 * Result of a successful `POST /api/tenant/data-explorer/query`. Columns are
 * derived from the first row's keys; rows are tuples in column order
 * so the SPA renders without re-keying on every cell access. `truncated`
 * reflects the server-side `LIMIT rowCap+1` check.
 */
export interface DataExplorerQueryResult {
  columns: Array<{ name: string; type: string }>;
  /** Tuples of values in column order. */
  rows: unknown[][];
  truncated: boolean;
  /** Wall-clock ms the query took on the server. */
  durationMs: number;
}

/**
 * Error envelope returned by `POST /api/tenant/data-explorer/query` and
 * `GET /api/tenant/data-explorer/schema`. Includes the `exId` correlation
 * tag so user-reported failures map to a single server log line.
 */
export interface DataExplorerError {
  /**
   * Server-side error code:
   *   `sql_rejected` — photon's policy gate refused the statement (400).
   *                    The client's schema allow-list, the dialect's
   *                    deny-list, or the single-SELECT rule; the engine
   *                    never saw it. `message` names which. Previously
   *                    thrown by an in-process validator that no longer
   *                    exists — the code and the status are unchanged.
   *   `timeout`       — 45 s statement timeout fired (408)
   *   `db_error`      — MotherDuck threw (500)
   *   `query_failed`  — pool acquire failed before the run (500)
   *   `auth_backend_unavailable` — service token unavailable (502)
   *   `audit_unavailable` — synchronous audit insert failed (503)
   *   `schema_unavailable` — schema endpoint upstream failed (503)
   *   `rate_limited`  — per-user 60 req/min cap (429)
   */
  code: string;
  message: string;
  exId?: string;
}

/** Promise-friendly Error subclass that carries the structured shape. */
export class DataExplorerApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: DataExplorerError,
  ) {
    super(payload.message);
    this.name = "DataExplorerApiError";
  }
}

/**
 * Run a read-only SELECT/WITH against the user's MotherDuck warehouse.
 *
 * Body shape mirrors `docs/legacy/plans/data-explorer.md` §"POST /api/tenant/data-explorer/query".
 * `rowCap` defaults to 1000 server-side; pass an explicit value to bump
 * up to the 10000 hard ceiling.
 */
export const runDataExplorerQuery = async (
  sql: string,
  rowCap?: number,
): Promise<DataExplorerQueryResult> => {
  const body: Record<string, unknown> = { sql };
  if (typeof rowCap === "number") body.rowCap = rowCap;
  const r = await apiFetch("/api/tenant/data-explorer/query", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const payload = (await r.json().catch(() => ({}))) as DataExplorerError;
    throw new DataExplorerApiError(r.status, {
      code: payload.code ?? "upstream",
      message: payload.message ?? `request failed with status ${r.status}`,
      exId: payload.exId,
    });
  }
  return (await r.json()) as DataExplorerQueryResult;
};

/** One table inside a schema. `kind` is "BASE TABLE" / "VIEW" etc.; columns ordered as DuckDB ordinal_position. */
export interface DataExplorerTable {
  name: string;
  kind: string;
  columns: Array<{ name: string; type: string }>;
}

/** One schema in the schema-tree response. */
export interface DataExplorerSchema {
  /** Database / catalog the schema belongs to (DuckDB three-part naming). */
  catalog: string;
  /** Schema name within the catalog. */
  name: string;
  tables: DataExplorerTable[];
}

/** Successful response shape from `GET /api/tenant/data-explorer/schema`. */
export interface DataExplorerSchemaResponse {
  schemas: DataExplorerSchema[];
  fetchedAt: string;
}

/**
 * Introspect the caller's MotherDuck catalog. Single fetch; the SPA
 * caches the result for the view's lifetime and re-fetches on a manual
 * "refresh" affordance in the schema panel header.
 */
export const getDataExplorerSchema = async (): Promise<DataExplorerSchemaResponse> => {
  const r = await apiFetch("/api/tenant/data-explorer/schema");
  if (!r.ok) {
    const payload = (await r.json().catch(() => ({}))) as DataExplorerError;
    throw new DataExplorerApiError(r.status, {
      code: payload.code ?? "upstream",
      message: payload.message ?? `schema fetch failed with status ${r.status}`,
      exId: payload.exId,
    });
  }
  return (await r.json()) as DataExplorerSchemaResponse;
};

/**
 * Quote a single SQL identifier — wrap in double-quotes, doubling any
 * embedded double-quote character per the SQL standard. The validator's
 * FROM-string rule only fires on single-quoted sources, so double-
 * quoted identifiers (the standard quoted-identifier form) pass.
 */
const quoteIdent = (s: string): string =>
  `"${s.replace(/"/g, '""')}"`;

/**
 * Compose a fully-qualified `"catalog"."schema"."table"` reference.
 * DuckDB / MotherDuck names tables in three parts; emitting just
 * `"schema"."table"` fails with a Catalog Error on any account where
 * the token can see more than one database.
 *
 * `catalog` may be empty for callers that haven't surfaced it yet —
 * the result then falls back to `"schema"."table"` for backwards
 * compatibility with single-database deployments.
 */
export const qualifiedTableName = (
  catalog: string,
  schema: string,
  table: string,
): string => {
  const qTable = quoteIdent(table);
  const qSchema = quoteIdent(schema);
  if (!catalog) return `${qSchema}.${qTable}`;
  return `${quoteIdent(catalog)}.${qSchema}.${qTable}`;
};

/**
 * Run a `SELECT count(*)` against a fully-qualified table. Used by the
 * schema panel's lazy row-count chip. Wraps `runDataExplorerQuery`
 * because the count query goes through the same validator + pool +
 * audit-log path; the panel renders the resulting scalar inline.
 *
 * Returns `null` when the query yields nothing — the panel then
 * renders an "err" badge so the failure is visible.
 */
export const fetchTableRowCount = async (
  catalog: string,
  schema: string,
  table: string,
): Promise<number | null> => {
  const sql = `SELECT count(*) AS n FROM ${qualifiedTableName(catalog, schema, table)}`;
  const result = await runDataExplorerQuery(sql, 1);
  if (result.rows.length === 0) return null;
  const value = result.rows[0]?.[0];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Fetch the insights-api version string from its public `/healthz`
 * endpoint, which surfaces the `VERSION` file the running service was
 * built against. Used by the dashboard footer so operators can confirm
 * which build is live without shelling into the container.
 *
 * Reads the origin from the build-time `VITE_INSIGHTS_API_URL` rather than the
 * old `/api/embed/runtime-config` round trip — that endpoint existed only
 * because the SPA had to ask its own backend where the API lived, and there is
 * no backend to ask now.
 *
 * Uses a bare `fetch`, deliberately **not** `apiFetch`: `/healthz` is
 * unauthenticated, and routing it through the wrapper would attach the session
 * cookie and, worse, turn a 401 into a forced re-login for a footer decoration.
 * Returns `null` on any failure so the caller leaves the slot empty rather than
 * surfacing an error.
 */
export const getInsightsApiVersion = async (): Promise<string | null> => {
  try {
    const r = await fetch(`${INSIGHTS_API_URL}/healthz`);
    if (!r.ok) return null;
    const body = (await r.json()) as { version?: unknown };
    return typeof body.version === "string" ? body.version : null;
  } catch {
    return null;
  }
};

/**
 * Absolute URL of insights-api's `/status` page, which the dashboard's Status
 * links point at.
 *
 * Now a pure string build from `VITE_INSIGHTS_API_URL`, so it cannot fail —
 * kept `async` returning `string | null` only to spare every caller a
 * signature change. Left as a follow-up to collapse into a plain constant once
 * the call sites are touched for another reason.
 */
export const getInsightsApiStatusUrl = async (): Promise<string | null> =>
  `${INSIGHTS_API_URL}/status`;
